import { useCallback, useEffect, useRef, useState } from "react";
import { getSessionTimeoutConfig } from "../config/sessionTimeout";
import {
  ACTIVITY_EVENTS,
  LAST_ACTIVITY_STORAGE_KEY,
  getInactivityPhase,
  getMsUntilNextPhase,
} from "../utils/inactivityTimeout";

const ACTIVITY_THROTTLE_MS = 1000;

export const useInactivityTimeout = ({
  enabled = true,
  onLogout,
  timeoutMs: timeoutMsOverride,
  warningMs: warningMsOverride,
} = {}) => {
  const { timeoutMs: defaultTimeoutMs, warningMs: defaultWarningMs } =
    getSessionTimeoutConfig();
  const timeoutMs = timeoutMsOverride ?? defaultTimeoutMs;
  const warningMs = warningMsOverride ?? defaultWarningMs;

  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [warningRemainingMs, setWarningRemainingMs] = useState(warningMs);

  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const warningVisibleRef = useRef(false);
  const enabledRef = useRef(enabled);
  const onLogoutRef = useRef(onLogout);
  const lastThrottleRef = useRef(0);

  enabledRef.current = enabled;
  onLogoutRef.current = onLogout;
  warningVisibleRef.current = isWarningVisible;

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const persistActivity = useCallback((timestamp) => {
    lastActivityRef.current = timestamp;
    try {
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(timestamp));
    } catch (error) {
      // Ignore storage quota / private-mode failures.
    }
  }, []);

  const startCountdown = useCallback((remainingMs) => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    setWarningRemainingMs(remainingMs);
    countdownRef.current = setInterval(() => {
      setWarningRemainingMs((prev) => Math.max(0, prev - 1000));
    }, 1000);
  }, []);

  const evaluateAndSchedule = useCallback(() => {
    if (!enabledRef.current) {
      return;
    }
    clearTimers();
    const now = Date.now();
    const phase = getInactivityPhase(
      lastActivityRef.current,
      now,
      timeoutMs,
      warningMs
    );

    if (phase === "expired") {
      setIsWarningVisible(false);
      warningVisibleRef.current = false;
      onLogoutRef.current?.("timeout");
      return;
    }

    if (phase === "warning") {
      setIsWarningVisible(true);
      warningVisibleRef.current = true;
      const remaining = getMsUntilNextPhase(
        lastActivityRef.current,
        now,
        timeoutMs,
        warningMs
      );
      startCountdown(remaining);
      timerRef.current = setTimeout(() => {
        setIsWarningVisible(false);
        warningVisibleRef.current = false;
        onLogoutRef.current?.("timeout");
      }, remaining);
      return;
    }

    setIsWarningVisible(false);
    warningVisibleRef.current = false;
    const untilWarning = getMsUntilNextPhase(
      lastActivityRef.current,
      now,
      timeoutMs,
      warningMs
    );
    timerRef.current = setTimeout(() => {
      evaluateAndSchedule();
    }, untilWarning);
  }, [clearTimers, startCountdown, timeoutMs, warningMs]);

  const resetInactivityTimer = useCallback(() => {
    warningVisibleRef.current = false;
    setIsWarningVisible(false);
    persistActivity(Date.now());
    evaluateAndSchedule();
  }, [evaluateAndSchedule, persistActivity]);

  const handleStayLoggedIn = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const handleInactivityLogout = useCallback(() => {
    onLogoutRef.current?.("manual");
  }, []);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setIsWarningVisible(false);
      warningVisibleRef.current = false;
      return undefined;
    }

    const rawLastActivity = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
    const stored = rawLastActivity == null ? NaN : Number(rawLastActivity);
    lastActivityRef.current = Number.isFinite(stored) && stored > 0 ? stored : Date.now();
    if (!(Number.isFinite(stored) && stored > 0)) {
      persistActivity(lastActivityRef.current);
    }

    evaluateAndSchedule();

    const onActivity = () => {
      if (!enabledRef.current || warningVisibleRef.current) {
        return;
      }
      const now = Date.now();
      if (now - lastThrottleRef.current < ACTIVITY_THROTTLE_MS) {
        return;
      }
      lastThrottleRef.current = now;
      persistActivity(now);
      evaluateAndSchedule();
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true, capture: true });
    });

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        evaluateAndSchedule();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    const onStorage = (event) => {
      if (event.key === LAST_ACTIVITY_STORAGE_KEY && event.newValue) {
        const timestamp = Number(event.newValue);
        if (Number.isFinite(timestamp)) {
          lastActivityRef.current = timestamp;
          if (!warningVisibleRef.current) {
            evaluateAndSchedule();
          }
        }
      }
      if (
        (event.key === "token" && !event.newValue) ||
        (event.key === null && !localStorage.getItem("token"))
      ) {
        onLogoutRef.current?.("cross-tab");
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity, { capture: true });
      });
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
      clearTimers();
    };
  }, [enabled, evaluateAndSchedule, persistActivity, clearTimers]);

  return {
    isWarningVisible,
    warningRemainingMs,
    handleStayLoggedIn,
    handleInactivityLogout,
    resetInactivityTimer,
  };
};
